/**
 * Opportunity Utility Functions
 * Helper functions for processing and formatting opportunity data
 */

import { GovtOpportunityEntity } from '../pages/OpportunitiesDashboard';

/**
 * Map opportunity_type to proposal stage
 * Based on keystone_rfp_agent_v2.yaml entity schema
 */
export function mapOpportunityTypeToStage(
  opportunityType?: string,
  procurementMethod?: string,
  competitionType?: string
): 'EOI' | 'RFP' | 'Stage 1' | 'Stage 2' | 'Stage 3' | 'Unknown' {
  if (!opportunityType) {
    return 'Unknown';
  }

  const typeLower = opportunityType.toLowerCase().trim();

  // EOI (Expression of Interest)
  if (
    typeLower.includes('eoi') ||
    typeLower.includes('expression of interest') ||
    typeLower.includes('request for expression of interest') ||
    typeLower.includes('rfeoi')
  ) {
    return 'EOI';
  }

  // Stage 1/2/3 detection
  if (typeLower.includes('stage 1') || typeLower.includes('stage i')) {
    return 'Stage 1';
  }
  if (typeLower.includes('stage 2') || typeLower.includes('stage ii')) {
    return 'Stage 2';
  }
  if (typeLower.includes('stage 3') || typeLower.includes('stage iii')) {
    return 'Stage 3';
  }

  // RFP (Request for Proposal) - default for most procurement types
  if (
    typeLower.includes('rfp') ||
    typeLower.includes('request for proposal') ||
    typeLower.includes('rfq') ||
    typeLower.includes('request for quotation') ||
    typeLower.includes('ifb') ||
    typeLower.includes('invitation for bid') ||
    typeLower.includes('rft') ||
    typeLower.includes('request for tender') ||
    typeLower.includes('tender') ||
    typeLower.includes('solicitation')
  ) {
    return 'RFP';
  }

  // Check procurement method as fallback
  if (procurementMethod) {
    const methodLower = procurementMethod.toLowerCase();
    if (methodLower.includes('competitive') || methodLower.includes('open')) {
      return 'RFP';
    }
  }

  return 'Unknown';
}

/**
 * Format duration string from various duration fields
 */
export function formatDuration(
  projectDuration?: string,
  contractDuration?: string,
  timelineWeeks?: number
): string {
  // Try project_duration first
  if (projectDuration) {
    // Handle formats like "12 weeks", "6 months", "2 years"
    const duration = projectDuration.trim();
    if (duration) {
      return duration;
    }
  }

  // Try contract_duration
  if (contractDuration) {
    const duration = contractDuration.trim();
    if (duration) {
      return duration;
    }
  }

  // Calculate from timeline_weeks
  if (timelineWeeks !== undefined && timelineWeeks !== null) {
    if (timelineWeeks < 4) {
      return `${timelineWeeks} week${timelineWeeks !== 1 ? 's' : ''}`;
    } else if (timelineWeeks < 52) {
      const months = Math.round(timelineWeeks / 4.33);
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.round(timelineWeeks / 52);
      return `${years} year${years !== 1 ? 's' : ''}`;
    }
  }

  return 'Not specified';
}

/**
 * Calculate days until deadline
 */
export function calculateDaysUntilDeadline(deadline?: string): number | null {
  if (!deadline) {
    return null;
  }

  try {
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    console.error('Error calculating days until deadline:', error);
    return null;
  }
}

/**
 * Get urgency level for deadline
 */
export function getDeadlineUrgency(daysUntil: number | null): 'expired' | 'urgent' | 'soon' | 'normal' {
  if (daysUntil === null) {
    return 'normal';
  }

  if (daysUntil < 0) {
    return 'expired';
  } else if (daysUntil <= 7) {
    return 'urgent';
  } else if (daysUntil <= 14) {
    return 'soon';
  } else {
    return 'normal';
  }
}

/**
 * Get best value estimate from opportunity data
 */
export function getOpportunityValue(opportunity: GovtOpportunityEntity['data']): number | null {
  // Priority: contract_value > estimated_value > value > budget_range_max
  if (opportunity.contract_value !== undefined && opportunity.contract_value !== null) {
    return opportunity.contract_value;
  }
  if (opportunity.estimated_value !== undefined && opportunity.estimated_value !== null) {
    return opportunity.estimated_value;
  }
  if (opportunity.value !== undefined && opportunity.value !== null) {
    return opportunity.value;
  }
  if (opportunity.budget_range_max !== undefined && opportunity.budget_range_max !== null) {
    return opportunity.budget_range_max;
  }
  return null;
}

/**
 * Get formatted project ID for display
 */
export function getProjectIdDisplay(opportunity: GovtOpportunityEntity): string {
  if (opportunity.project_id) {
    return opportunity.project_id;
  }
  if (opportunity.data.opportunity_id) {
    return opportunity.data.opportunity_id;
  }
  if (opportunity.data.solicitation_number) {
    return opportunity.data.solicitation_number;
  }
  if (opportunity.data.reference_number) {
    return opportunity.data.reference_number;
  }
  return 'N/A';
}

/**
 * Get stage badge color
 */
export function getStageBadgeColor(
  stage: 'EOI' | 'RFP' | 'Stage 1' | 'Stage 2' | 'Stage 3' | 'Unknown'
): string {
  switch (stage) {
    case 'EOI':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800';
    case 'RFP':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    case 'Stage 1':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
    case 'Stage 2':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
    case 'Stage 3':
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
    default:
      return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
  }
}

/**
 * Enhance opportunity entity with computed fields
 */
export function enhanceOpportunityEntity(
  opportunity: GovtOpportunityEntity
): GovtOpportunityEntity {
  const enhanced = { ...opportunity };

  // Calculate proposal stage
  if (!enhanced.data.proposal_stage) {
    enhanced.data.proposal_stage = mapOpportunityTypeToStage(
      enhanced.data.opportunity_type,
      enhanced.data.procurement_method,
      enhanced.data.competition_type
    );
  }

  // Format duration
  if (!enhanced.data.duration_display) {
    enhanced.data.duration_display = formatDuration(
      enhanced.data.project_duration,
      enhanced.data.contract_duration,
      enhanced.data.timeline_weeks
    );
  }

  // Calculate days until deadline if not present
  if (enhanced.data.days_until_deadline === undefined && enhanced.data.deadline) {
    enhanced.data.days_until_deadline = calculateDaysUntilDeadline(enhanced.data.deadline);
  }

  // Set best value estimate
  if (!enhanced.data.value) {
    const bestValue = getOpportunityValue(enhanced.data);
    if (bestValue !== null) {
      enhanced.data.value = bestValue;
    }
  }

  // Format location field - remove asterisks and split by commas
  if (enhanced.data.location) {
    enhanced.data.location = formatLocation(enhanced.data.location);
  }

  // Clean title field - remove non-Latin characters
  if (enhanced.data.title) {
    enhanced.data.title = cleanText(enhanced.data.title);
  }

  return enhanced;
}

/**
 * Format location string - remove asterisks and split terms by commas
 * Handles patterns like "*Nunavut Territory *Iqaluit" -> "Nunavut Territory, Iqaluit"
 */
export function formatLocation(location: string): string {
  if (!location) {
    return '';
  }

  // Split by asterisks first (asterisks seem to be separators)
  // Pattern: "*Location1 *Location2" -> ["", "Location1 ", "Location2"]
  const parts = location
    .split(/\s*\*\s*/) // Split by asterisk (with optional whitespace)
    .map(part => part.trim())
    .filter(part => part.length > 0);

  // If we have multiple parts, join with commas
  if (parts.length > 1) {
    return parts.join(', ');
  }

  // If only one part, remove any remaining asterisks and return
  return location.replace(/\*/g, '').trim();
}

/**
 * Clean text by removing non-Latin characters and keeping only ASCII/Latin characters
 * Removes Unicode characters like Inuktitut script, emojis, etc.
 * 
 * Example: "INVITATION TO SUBMIT... ᖃᐃᖅᑯᔨᓂᖅ..." -> "INVITATION TO SUBMIT..."
 */
export function cleanText(text: string): string {
  if (!text) {
    return '';
  }

  // Remove Inuktitut and other Canadian Aboriginal Syllabics (U+1400-U+167F)
  // Keep all printable ASCII characters (0x20-0x7E) and common extended Latin characters
  // This is more conservative - only removes the problematic Inuktitut script
  
  let cleaned = text
    .replace(/[\u1400-\u167F]/g, '') // Remove Inuktitut Unified Canadian Aboriginal Syllabics
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim();
  
  // Only remove if the result is empty or mostly non-printable
  // Otherwise keep the text as-is (just with Inuktitut removed)
  if (cleaned.length === 0) {
    return text.trim(); // Fallback to original if cleaning removed everything
  }
  
  return cleaned;
}

